import { isAttributeEnabledIdentifier, CIO_SUBSCRIPTION_PREF_KEY, type IdentifiersConfig } from '@/features/people';
import { isObjectAttributeIdentifier } from '@/services/db/object/entity';
import { RESTRICTED_ATTRIBUTES } from '@/utils/attributes';
import { attributeContentUsageDeleteChecks, firstAttributeDeleteBlockReason } from './delete-blocks';
import { usageCountsLookup, type DataIndexUsageCounts } from './usage';
import { FALLBACK_OBJECT_TYPE_ID } from './types';

/**
 * Why a selection of attributes cannot be deleted.
 *
 * Deletion is permanent and asynchronous, so the backend refuses anything
 * still referenced and the list has to say which reference is in the way
 * before anyone commits to it. The reasons are the same ones the item page
 * gives, in the same order, so an attribute never has two explanations.
 */

/** What a workspace calls its automations, which two of the reasons are named after. */
export type DeleteReasonTerms = {
  /** "a campaign", as it reads mid-sentence. */
  campaignIndefinite: string;
  campaignsPlural: string;
  /** "a newsletter", as it reads mid-sentence. */
  newsletterIndefinite: string;
  newslettersPlural: string;
};

/** Everything the gate needs to know about one selection. */
export type BulkDeleteGate = {
  /** Attribute names only: an event cannot be deleted at all. */
  names: readonly string[];
  /** The object type the list is showing, or none for a person's attributes. */
  objectTypeId: number;
  /** Whether the viewer may edit attribute metadata. */
  canEdit: boolean;
  /** True while the usage read has not answered; a gate without usage is a guess. */
  isUsageLoading: boolean;
  /**
   * True when the usage read failed. Absent usage reads as zero everywhere,
   * so without this the gate would offer to delete attributes it never
   * learned were in use.
   */
  isUsageError: boolean;
  /** How many things use each attribute, by name. */
  usage: Record<string, DataIndexUsageCounts> | undefined;
  /** The attributes the workspace identifies people by. */
  identifiers: IdentifiersConfig | null | undefined;
  terms: DeleteReasonTerms;
};

/** The attribute is one of Customer.io's own, and `created_at` is the exception. */
function isReservedForDelete(name: string): boolean {
  return RESTRICTED_ATTRIBUTES.includes(name) && name !== 'created_at';
}

/** The key an object is identified by, which is not an attribute anyone chose. */
const OBJECT_TYPE_ID_KEY = 'object_type_id';

function isIdentifier(name: string, identifiers: IdentifiersConfig | null | undefined): boolean {
  return (
    isAttributeEnabledIdentifier(name, identifiers) || isObjectAttributeIdentifier(name) || name === OBJECT_TYPE_ID_KEY
  );
}

/**
 * Why one attribute cannot be deleted, or nothing when it can.
 *
 * Only a person's attributes can be deleted at all, so only they are checked
 * against the content surfaces: an object's attributes cannot appear in a
 * template, and the endpoint would refuse them anyway.
 */
export function attributeDeleteBlockReason(name: string, gate: BulkDeleteGate): string | undefined {
  const isProfileAttribute = gate.objectTypeId === FALLBACK_OBJECT_TYPE_ID;
  const countOf = usageCountsLookup(gate.usage?.[name]);

  return firstAttributeDeleteBlockReason([
    { when: isIdentifier(name, gate.identifiers), reason: 'This attribute is an identifier and cannot be deleted.' },
    { when: isReservedForDelete(name), reason: 'This attribute is reserved and cannot be deleted.' },
    {
      when: countOf('segments') !== 0,
      reason: 'This attribute is currently used in a segment. To delete it, first remove it from all segments.',
    },
    {
      when: countOf('campaigns') !== 0,
      reason: `This attribute is currently used in ${gate.terms.campaignIndefinite}. To delete it, first remove it from all ${gate.terms.campaignsPlural}.`,
    },
    ...attributeContentUsageDeleteChecks(countOf, {
      withTemplatesSnippetsLayouts: isProfileAttribute,
      withDesignStudio: isProfileAttribute,
    }),
    {
      when: isProfileAttribute && countOf('newsletters') !== 0,
      reason: `This attribute is currently used in ${gate.terms.newsletterIndefinite}. To delete it, first remove it from all ${gate.terms.newslettersPlural}.`,
    },
    { when: name.startsWith(CIO_SUBSCRIPTION_PREF_KEY), reason: 'Subscription topics cannot be deleted.' },
    { when: !isProfileAttribute, reason: 'Object attributes cannot be deleted.' },
  ]);
}

/**
 * Why the selection as a whole cannot be deleted, or nothing when it can.
 *
 * One blocked attribute is named, because deselecting it is the obvious next
 * move. Several are not: the list of names would be longer than the sentence
 * explaining it, and they are all in the way for their own reasons.
 */
export function bulkDeleteBlockReason(gate: BulkDeleteGate): string | undefined {
  if (!gate.canEdit) {
    return 'You do not have permission to delete attributes.';
  }
  if (gate.isUsageLoading) {
    return 'Wait for usage data to load.';
  }
  if (gate.isUsageError) {
    return 'Could not check what uses these attributes. Refresh and try again.';
  }

  const blocked = gate.names.filter((name) => attributeDeleteBlockReason(name, gate) != null);

  if (blocked.length === 0) {
    return undefined;
  }
  const [only] = blocked;
  if (blocked.length === 1 && only != null) {
    return `"${only}" cannot be deleted: ${(attributeDeleteBlockReason(only, gate) ?? '').toLowerCase()}`;
  }
  return "In-use or reserved attributes can't be deleted. Deselect them to continue.";
}
